import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query, orderBy } from './firestore-compat';

// ── Design tokens (dark) ────────────────────────────────────────────────────
const D = {
  bg:       '#0f172a',
  surface:  '#1e293b',
  card:     '#1e293b',
  border:   '#334155',
  borderL:  '#475569',
  text:     '#f1f5f9',
  sub:      '#94a3b8',
  muted:    '#64748b',
  blue:     '#38bdf8',
  blueBg:   'rgba(56,189,248,0.12)',
  green:    '#4ade80',
  greenBg:  'rgba(74,222,128,0.12)',
  amber:    '#fbbf24',
  amberBg:  'rgba(251,191,36,0.12)',
  red:      '#f87171',
  redBg:    'rgba(248,113,113,0.12)',
  purple:   '#a78bfa',
  purpleBg: 'rgba(167,139,250,0.12)',
  teal:     '#2dd4bf',
  tealBg:   'rgba(45,212,191,0.12)',
};

// ── Field auto-detection ─────────────────────────────────────────────────────
function detectField(keys, ...kws) {
  const norm = s => s.toLowerCase().replace(/[\s_\-\.\/]/g, '');
  return keys.find(k => kws.some(w => norm(k).includes(norm(w)))) || null;
}

function guessFields(headers) {
  return {
    nome:        detectField(headers, 'nomepaciente', 'nome', 'paciente', 'beneficiario'),
    matricula:   detectField(headers, 'matricula', 'prontuario', 'codigo', 'id', 'carteirinha'),
    operadora:   detectField(headers, 'operadora', 'plano', 'convenio', 'unimed', 'seguradora'),
    status:      detectField(headers, 'status', 'situacao', 'estado'),
    competencia: detectField(headers, 'competencia', 'mes', 'periodo', 'referencia'),
    data:        detectField(headers, 'data', 'abertura', 'criacao', 'entrada'),
    cid:         detectField(headers, 'cid', 'diagnostico', 'patologia', 'doenca'),
    profissional:detectField(headers, 'profissional', 'medico', 'enfermeiro', 'tecnico', 'responsavel'),
    especialidade:detectField(headers, 'especialidade', 'area', 'setor', 'departamento'),
    tipo:        detectField(headers, 'tipo', 'modalidade', 'categoria', 'procedimento'),
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function parseDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  const m1 = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m1) return new Date(+m1[3], +m1[2]-1, +m1[1]);
  const m2 = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return new Date(+m2[1], +m2[2]-1, +m2[3]);
  return null;
}

function fmtN(n) { return Number(n).toLocaleString('pt-BR'); }

function countBy(rows, field) {
  const map = {};
  rows.forEach(r => {
    const v = (r[field] || 'N/D').trim() || 'N/D';
    map[v] = (map[v] || 0) + 1;
  });
  return Object.entries(map)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

const COLORS = [D.blue, D.green, D.amber, D.purple, D.teal, D.red, '#fb923c', '#e879f9', '#a3e635', '#22d3ee'];

// ── Sub-components ────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color = D.blue, icon }) {
  return (
    <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: 12, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: D.sub, textTransform: 'uppercase', letterSpacing: .6 }}>{label}</span>
        {icon && <span style={{ fontSize: '1rem' }}>{icon}</span>}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: D.muted }}>{sub}</div>}
    </div>
  );
}

function HBar({ data, colorFn, field }) {
  if (!data.length) return <Empty />;
  const max = data[0].value;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {data.slice(0, 12).map((d, i) => {
        const pct = max > 0 ? Math.round((d.value / max) * 100) : 0;
        const c = colorFn ? colorFn(d.name, i) : COLORS[i % COLORS.length];
        return (
          <div key={d.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: '0.76rem', color: D.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{d.name}</span>
              <span style={{ fontSize: '0.76rem', fontWeight: 700, color: c }}>{fmtN(d.value)}</span>
            </div>
            <div style={{ background: D.border, borderRadius: 4, height: 7, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: c, borderRadius: 4, transition: 'width .4s' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PieSimple({ data }) {
  if (!data.length) return <Empty />;
  const total = data.reduce((a, b) => a + b.value, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {data.slice(0, 10).map((d, i) => {
        const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : 0;
        const c = COLORS[i % COLORS.length];
        return (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: c, flexShrink: 0 }} />
            <span style={{ fontSize: '0.76rem', color: D.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
            <span style={{ fontSize: '0.72rem', color: D.sub, flexShrink: 0 }}>{fmtN(d.value)} ({pct}%)</span>
          </div>
        );
      })}
    </div>
  );
}

function Card({ title, children, style }) {
  return (
    <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: 14, padding: '18px 20px', ...style }}>
      {title && <h3 style={{ margin: '0 0 14px', fontSize: '0.82rem', fontWeight: 700, color: D.sub, textTransform: 'uppercase', letterSpacing: .6 }}>{title}</h3>}
      {children}
    </div>
  );
}

function Empty() {
  return <div style={{ color: D.muted, fontSize: '0.78rem', textAlign: 'center', padding: '20px 0' }}>— sem dados —</div>;
}

const EMPTY_FILTROS = { operadora:'', status:'', tipo:'', especialidade:'', profissional:'', dataIni:'', dataFim:'' };

// ── Main ─────────────────────────────────────────────────────────────────────
const TIPOS_PRONTUARIO = [
  { k: 'sintetico', l: '📊 Prontuário Sintético' },
  { k: 'analitico', l: '📋 Prontuário Analítico' },
];

export default function DashboardProntuario() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabTipo, setTabTipo] = useState('sintetico');
  const [selComp, setSelComp] = useState('__all__');
  const [search, setSearch] = useState('');
  const [filtros, setFiltros] = useState(EMPTY_FILTROS);
  const [tablePage, setTablePage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const PAGE = 50;

  const setF = (key, val) => { setFiltros(prev => ({ ...prev, [key]: val })); setTablePage(1); };
  const clearFilters = () => { setFiltros(EMPTY_FILTROS); setSearch(''); setTablePage(1); };
  const hasFilter = Object.values(filtros).some(Boolean) || search.trim();

  useEffect(() => {
    const q = query(collection(db, 'prontuario_uploads'), orderBy('criadoEm', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setUploads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  // Uploads do tipo selecionado (Sintético/Analítico) — uploads antigos sem "tipo" contam como Sintético
  const uploadsDoTipo = useMemo(() => uploads.filter(u => (u.tipo || 'sintetico') === tabTipo), [uploads, tabTipo]);

  // Competências disponíveis
  const competencias = useMemo(() => {
    const set = new Set(uploadsDoTipo.map(u => u.competencia).filter(Boolean));
    return ['__all__', ...Array.from(set).sort((a, b) => b.localeCompare(a))];
  }, [uploadsDoTipo]);

  // Todas as linhas do período selecionado
  const rows = useMemo(() => {
    const uFiltered = selComp === '__all__'
      ? uploadsDoTipo
      : uploadsDoTipo.filter(u => u.competencia === selComp);
    return uFiltered.flatMap(u => u.rows || []);
  }, [uploadsDoTipo, selComp]);

  // Detectar campos
  const fields = useMemo(() => {
    const allHeaders = uploadsDoTipo.flatMap(u => u.headers || []);
    const unique = [...new Set(allHeaders)];
    return guessFields(unique);
  }, [uploadsDoTipo]);

  // Todos os headers detectados (union)
  const allHeaders = useMemo(() => {
    const set = new Set(uploadsDoTipo.flatMap(u => u.headers || []));
    return [...set];
  }, [uploadsDoTipo]);

  // KPIs
  const kpis = useMemo(() => {
    const totalReg = rows.length;
    const uniqPacientes = fields.nome
      ? new Set(rows.map(r => r[fields.nome]).filter(Boolean)).size
      : null;
    const uniqOps = fields.operadora
      ? new Set(rows.map(r => r[fields.operadora]).filter(Boolean)).size
      : null;
    const uniqComp = new Set(uploadsDoTipo.map(u => u.competencia).filter(Boolean)).size;
    return { totalReg, uniqPacientes, uniqOps, uniqComp };
  }, [rows, fields, uploadsDoTipo]);

  // Unique values para filtros dropdown
  const uniqueVals = useMemo(() => {
    const pick = (field) => field
      ? [...new Set(rows.map(r => (r[field] || '').trim()).filter(Boolean))].sort()
      : [];
    return {
      operadora:    pick(fields.operadora),
      status:       pick(fields.status),
      tipo:         pick(fields.tipo),
      especialidade:pick(fields.especialidade),
      profissional: pick(fields.profissional),
    };
  }, [rows, fields]);

  // Distribuições
  const byOperadora = useMemo(() => fields.operadora ? countBy(rows, fields.operadora) : [], [rows, fields]);
  const byStatus    = useMemo(() => fields.status    ? countBy(rows, fields.status)    : [], [rows, fields]);
  const byTipo      = useMemo(() => fields.tipo      ? countBy(rows, fields.tipo)      : [], [rows, fields]);
  const byEspec     = useMemo(() => fields.especialidade ? countBy(rows, fields.especialidade) : [], [rows, fields]);
  const byProf      = useMemo(() => fields.profissional  ? countBy(rows, fields.profissional)  : [], [rows, fields]);

  // Distribuição por mês (se tiver campo de data)
  const porMes = useMemo(() => {
    if (!fields.data) return [];
    const map = {};
    rows.forEach(r => {
      const d = parseDate(r[fields.data]);
      if (!d) return;
      const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      map[k] = (map[k] || 0) + 1;
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([name, value]) => ({ name, value }));
  }, [rows, fields]);

  // Tabela com busca + filtros coluna
  const filteredRows = useMemo(() => {
    let r = rows;
    if (filtros.operadora    && fields.operadora)    r = r.filter(x => (x[fields.operadora]    || '').trim() === filtros.operadora);
    if (filtros.status       && fields.status)       r = r.filter(x => (x[fields.status]       || '').trim() === filtros.status);
    if (filtros.tipo         && fields.tipo)         r = r.filter(x => (x[fields.tipo]         || '').trim() === filtros.tipo);
    if (filtros.especialidade && fields.especialidade) r = r.filter(x => (x[fields.especialidade] || '').trim() === filtros.especialidade);
    if (filtros.profissional  && fields.profissional)  r = r.filter(x => (x[fields.profissional]  || '').trim() === filtros.profissional);
    if (filtros.dataIni && fields.data) r = r.filter(x => { const d = parseDate(x[fields.data]); return d && d >= new Date(filtros.dataIni + 'T00:00'); });
    if (filtros.dataFim && fields.data) r = r.filter(x => { const d = parseDate(x[fields.data]); return d && d <= new Date(filtros.dataFim + 'T23:59:59'); });
    if (search.trim()) { const q = search.toLowerCase(); r = r.filter(x => Object.values(x).some(v => String(v).toLowerCase().includes(q))); }
    return r;
  }, [rows, fields, filtros, search]);

  const totalPag = Math.max(1, Math.ceil(filteredRows.length / PAGE));
  const pageRows = filteredRows.slice((tablePage - 1) * PAGE, tablePage * PAGE);
  const tableHeaders = allHeaders.slice(0, 12); // limit visible cols

  const fmtComp = c => c === '__all__' ? 'Todas as competências' : c;

  if (loading) {
    return (
      <div style={{ background: D.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: D.sub, fontSize: '0.9rem' }}>Carregando prontuários…</div>
      </div>
    );
  }

  const TabSwitcher = ({ compact }) => (
    <div style={{ display: 'flex', gap: 6, marginBottom: compact ? 20 : 16 }}>
      {TIPOS_PRONTUARIO.map(t => (
        <button key={t.k} onClick={() => { setTabTipo(t.k); setSelComp('__all__'); setTablePage(1); }}
          style={{
            padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem',
            border: `1.5px solid ${tabTipo === t.k ? '#6366f1' : D.border}`,
            background: tabTipo === t.k ? '#eef2ff' : D.surface,
            color: tabTipo === t.k ? '#4338ca' : D.muted,
          }}>
          {t.l}
        </button>
      ))}
    </div>
  );

  if (!uploadsDoTipo.length) {
    return (
      <div style={{ background: D.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
        <TabSwitcher compact />
        <div style={{ fontSize: 48 }}>📄</div>
        <div style={{ color: D.text, fontWeight: 700, fontSize: '1.1rem' }}>
          Nenhum {tabTipo === 'sintetico' ? 'Prontuário Sintético' : 'Prontuário Analítico'} importado
        </div>
        <div style={{ color: D.muted, fontSize: '0.85rem' }}>Importe dados em Uploads → Prontuários</div>
      </div>
    );
  }

  return (
    <div style={{ background: D.bg, minHeight: '100vh', padding: '24px 28px', fontFamily: 'system-ui, -apple-system, sans-serif', color: D.text }}>

      <TabSwitcher />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: D.text }}>
            📄 Dashboard de Prontuários — {tabTipo === 'sintetico' ? 'Sintético' : 'Analítico'}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: D.muted }}>
            {fmtN(kpis.totalReg)} registros · {kpis.uniqComp} {kpis.uniqComp === 1 ? 'competência' : 'competências'} importadas
          </p>
        </div>

        {/* Seletor de competência + toggle filtros */}
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <select
            value={selComp}
            onChange={e => { setSelComp(e.target.value); setTablePage(1); }}
            style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: 8, color: D.text, padding: '8px 14px', fontSize: '0.84rem', cursor: 'pointer', outline: 'none' }}
          >
            {competencias.map(c => (
              <option key={c} value={c}>{fmtComp(c)}</option>
            ))}
          </select>
          <button onClick={() => setShowFilters(s => !s)}
            style={{ background: showFilters ? '#6366f1' : D.surface, border: `1px solid ${showFilters ? '#6366f1' : D.border}`, borderRadius: 8, color: showFilters ? '#fff' : D.text, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
            🔎 Filtros{hasFilter ? ' ●' : ''}
          </button>
          {hasFilter && (
            <button onClick={clearFilters}
              style={{ background:'none', border:`1px solid ${D.border}`, borderRadius:8, color:D.sub, padding:'8px 12px', fontSize:'0.78rem', cursor:'pointer' }}>
              ✕ Limpar
            </button>
          )}
        </div>
      </div>

      {/* Painel de filtros */}
      {showFilters && (
        <div style={{ background: D.surface, border:`1px solid ${D.border}`, borderRadius:12, padding:'16px 20px', marginBottom:20 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:12 }}>
            {/* Busca geral */}
            <div>
              <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:D.sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>Busca geral</label>
              <input value={search} onChange={e => { setSearch(e.target.value); setTablePage(1); }} placeholder="Buscar em todos os campos…"
                style={{ width:'100%', background:D.bg, border:`1px solid ${D.border}`, borderRadius:8, color:D.text, padding:'7px 11px', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }} />
            </div>

            {/* Operadora */}
            {uniqueVals.operadora.length > 0 && (
              <div>
                <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:D.sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
                  {fields.operadora || 'Operadora'}
                </label>
                <select value={filtros.operadora} onChange={e => setF('operadora', e.target.value)}
                  style={{ width:'100%', background:D.bg, border:`1px solid ${D.border}`, borderRadius:8, color:D.text, padding:'7px 11px', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}>
                  <option value="">Todas</option>
                  {uniqueVals.operadora.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
            )}

            {/* Status */}
            {uniqueVals.status.length > 0 && (
              <div>
                <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:D.sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
                  {fields.status || 'Status'}
                </label>
                <select value={filtros.status} onChange={e => setF('status', e.target.value)}
                  style={{ width:'100%', background:D.bg, border:`1px solid ${D.border}`, borderRadius:8, color:D.text, padding:'7px 11px', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}>
                  <option value="">Todos</option>
                  {uniqueVals.status.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
            )}

            {/* Tipo */}
            {uniqueVals.tipo.length > 0 && (
              <div>
                <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:D.sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
                  {fields.tipo || 'Tipo'}
                </label>
                <select value={filtros.tipo} onChange={e => setF('tipo', e.target.value)}
                  style={{ width:'100%', background:D.bg, border:`1px solid ${D.border}`, borderRadius:8, color:D.text, padding:'7px 11px', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}>
                  <option value="">Todos</option>
                  {uniqueVals.tipo.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
            )}

            {/* Especialidade */}
            {uniqueVals.especialidade.length > 0 && (
              <div>
                <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:D.sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
                  {fields.especialidade || 'Especialidade'}
                </label>
                <select value={filtros.especialidade} onChange={e => setF('especialidade', e.target.value)}
                  style={{ width:'100%', background:D.bg, border:`1px solid ${D.border}`, borderRadius:8, color:D.text, padding:'7px 11px', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}>
                  <option value="">Todas</option>
                  {uniqueVals.especialidade.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
            )}

            {/* Profissional */}
            {uniqueVals.profissional.length > 0 && (
              <div>
                <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:D.sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
                  {fields.profissional || 'Profissional'}
                </label>
                <select value={filtros.profissional} onChange={e => setF('profissional', e.target.value)}
                  style={{ width:'100%', background:D.bg, border:`1px solid ${D.border}`, borderRadius:8, color:D.text, padding:'7px 11px', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }}>
                  <option value="">Todos</option>
                  {uniqueVals.profissional.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
            )}

            {/* Período (data) */}
            {fields.data && (
              <>
                <div>
                  <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:D.sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>Data inicial</label>
                  <input type="date" value={filtros.dataIni} onChange={e => setF('dataIni', e.target.value)}
                    style={{ width:'100%', background:D.bg, border:`1px solid ${D.border}`, borderRadius:8, color:D.text, padding:'7px 11px', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'0.68rem', fontWeight:700, color:D.sub, textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>Data final</label>
                  <input type="date" value={filtros.dataFim} onChange={e => setF('dataFim', e.target.value)}
                    style={{ width:'100%', background:D.bg, border:`1px solid ${D.border}`, borderRadius:8, color:D.text, padding:'7px 11px', fontSize:'0.82rem', outline:'none', boxSizing:'border-box' }} />
                </div>
              </>
            )}
          </div>

          {hasFilter && (
            <div style={{ marginTop:12, fontSize:'0.74rem', color:D.sub }}>
              {filteredRows.length} de {rows.length} registros após filtros
            </div>
          )}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        <KpiCard label="Total de Registros" value={fmtN(kpis.totalReg)} color={D.blue} icon="📋" />
        {kpis.uniqPacientes !== null && (
          <KpiCard label="Pacientes Únicos" value={fmtN(kpis.uniqPacientes)} color={D.green} icon="👤" />
        )}
        {kpis.uniqOps !== null && (
          <KpiCard label="Operadoras" value={fmtN(kpis.uniqOps)} color={D.amber} icon="🏥" />
        )}
        <KpiCard
          label="Competências"
          value={kpis.uniqComp}
          sub={selComp !== '__all__' ? `Filtrando: ${selComp}` : 'todas carregadas'}
          color={D.purple}
          icon="📅"
        />
        <KpiCard
          label="Arquivos Importados"
          value={uploadsDoTipo.length}
          sub={`último: ${uploadsDoTipo[0]?.nomeArquivo ?? '—'}`}
          color={D.teal}
          icon="📁"
        />
      </div>

      {/* Gráficos linha 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
        {byOperadora.length > 0 && (
          <Card title={`Por ${fields.operadora}`}>
            <HBar data={byOperadora} />
          </Card>
        )}
        {byStatus.length > 0 && (
          <Card title={`Por ${fields.status}`}>
            <PieSimple data={byStatus} />
          </Card>
        )}
        {byTipo.length > 0 && (
          <Card title={`Por ${fields.tipo}`}>
            <HBar data={byTipo} />
          </Card>
        )}
      </div>

      {/* Gráficos linha 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
        {byEspec.length > 0 && (
          <Card title={`Por ${fields.especialidade}`}>
            <HBar data={byEspec} />
          </Card>
        )}
        {byProf.length > 0 && (
          <Card title={`Por ${fields.profissional}`}>
            <HBar data={byProf} colorFn={(_, i) => COLORS[(i + 3) % COLORS.length]} />
          </Card>
        )}
        {porMes.length > 1 && (
          <Card title="Registros por mês">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {porMes.map((d, i) => {
                const max = Math.max(...porMes.map(x => x.value));
                const pct = max > 0 ? Math.round((d.value / max) * 100) : 0;
                return (
                  <div key={d.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: '0.76rem', color: D.text }}>{d.name}</span>
                      <span style={{ fontSize: '0.76rem', fontWeight: 700, color: D.blue }}>{fmtN(d.value)}</span>
                    </div>
                    <div style={{ background: D.border, borderRadius: 4, height: 7, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: D.blue, borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Histórico de uploads */}
      <Card title="Histórico de importações" style={{ marginBottom: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${D.border}` }}>
                {['Competência', 'Arquivo', 'Linhas', 'Colunas', 'Importado em'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: D.sub, fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: .5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {uploadsDoTipo.map(u => {
                const dt = u.criadoEm?.toDate ? u.criadoEm.toDate() : null;
                return (
                  <tr key={u.id} style={{ borderBottom: `1px solid ${D.border}30` }}>
                    <td style={{ padding: '8px 12px', color: D.blue, fontWeight: 700 }}>{u.competencia || '—'}</td>
                    <td style={{ padding: '8px 12px', color: D.text, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.nomeArquivo || '—'}</td>
                    <td style={{ padding: '8px 12px', color: D.sub }}>{fmtN(u.totalLinhas || 0)}</td>
                    <td style={{ padding: '8px 12px', color: D.sub }}>{(u.headers || []).length}</td>
                    <td style={{ padding: '8px 12px', color: D.muted, whiteSpace: 'nowrap' }}>
                      {dt ? dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Tabela de dados */}
      {rows.length > 0 && (
        <Card title="Registros" style={{ marginBottom: 0 }}>
          {/* Paginação */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', color: D.muted, whiteSpace: 'nowrap' }}>
              {fmtN(filteredRows.length)} registro{filteredRows.length !== 1 ? 's' : ''}{hasFilter ? ` (filtrado de ${fmtN(rows.length)})` : ''} · pág {tablePage}/{totalPag}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setTablePage(p => Math.max(1, p-1))} disabled={tablePage <= 1}
                style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: 7, color: D.text, padding: '6px 12px', cursor: tablePage <= 1 ? 'default' : 'pointer', opacity: tablePage <= 1 ? .4 : 1, fontSize: '0.78rem' }}>
                ← Ant.
              </button>
              <button onClick={() => setTablePage(p => Math.min(totalPag, p+1))} disabled={tablePage >= totalPag}
                style={{ background: D.surface, border: `1px solid ${D.border}`, borderRadius: 7, color: D.text, padding: '6px 12px', cursor: tablePage >= totalPag ? 'default' : 'pointer', opacity: tablePage >= totalPag ? .4 : 1, fontSize: '0.78rem' }}>
                Próx. →
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.76rem' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${D.border}` }}>
                  {tableHeaders.map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: D.sub, fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: .4 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${D.border}20`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                    {tableHeaders.map(h => (
                      <td key={h} style={{ padding: '7px 10px', color: D.text, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r[h] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {allHeaders.length > 12 && (
            <div style={{ marginTop: 10, fontSize: '0.7rem', color: D.muted }}>
              Mostrando {tableHeaders.length} de {allHeaders.length} colunas disponíveis.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
